import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { AppointmentControllerApi } from "../../services";
import { AppointmentDetails } from "../../types/appointment-types";
import {
  DGT_Pagination,
  asPaginatedContent,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  getXtenantId,
  useBaseContent,
} from "../../utils/utils";

interface UseAppointmentReportListProps {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  searchKey?: string;
  startDate?: string;
  endDate?: string;
  statuses?: Array<string>;
  modes?: Array<string>;
  providerUuid?: Array<string>;
  locationUuid?: Array<string>;
  scheduledSources?: Array<string>;
  providerGroupId?: string | null;
}

/**
 * @description Hook for fetching appointment report listing data (v2-list API)
 */
export function useAppointmentReportList(
  props: UseAppointmentReportListProps
): useBaseContent<AppointmentDetails> {
  const prevPage = useRef<number>(props.pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination: props.pagination,
    searchKey: props.searchKey,
    prevPage,
  });

  const pageSize = getPageSize(props.searchKey, props.pagination);
  const sortData = props.gridSorting?.[0];

  return useQuery({
    queryKey: [
      "appointment-report-list",
      pageSize,
      pageIndex,
      sortData,
      props.searchKey,
      props.startDate,
      props.endDate,
      props.statuses,
      props.modes,
      props.providerUuid,
      props.locationUuid,
      props.scheduledSources,
    ],
    enabled: !!props.providerGroupId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      new AppointmentControllerApi(getConfigObject())
        .getAppointmentList(
          pageIndex,
          pageSize,
          sortData?.field ?? undefined,
          sortData?.sort ?? undefined,
          props.searchKey,
          props.startDate,
          props.endDate,
          props.statuses,
          props.modes,
          props.providerUuid,
          undefined, // patientUuid
          props.locationUuid,
          undefined,
          props.providerGroupId ?? getXtenantId()
        )
        .then((res) => asPaginatedContent<AppointmentDetails>(res?.data)),
  });
}
